// src/lkp/SmeHelp.tsx
//
// SME Help - one place that explains the reference-data workflow in plain language.
//   * "What to do now"   - context-aware: reads the live overview counts for the ACTIVE step
//   * "Buttons explained" - every action of the active step and what happens behind it
//   * "Terms"             - searchable glossary of every term used on these screens
// Self-contained (own styles). Opened from the "? SME Help" button in the workbench top bar.

import React, { useMemo, useState } from "react";

type Step = 1 | 2 | 3;
interface Props { open: boolean; onClose: () => void; step: Step; overview?: any; }

const n = (v: any) => (typeof v === "number" ? v : 0);

// ---- what to do now (context-aware) -----------------------------------------------------
function nowFor(step: Step, ov: any): { title: string; points: string[] } {
  if (step === 1) {
    const s = ov?.step1?.summary || {};
    const st = s.by_status || {};
    const total = n(s.total);
    const open = Math.max(0, total - n(st.ACCEPTED) - n(st.REJECTED));
    return {
      title: "Step 1 - decide which fields are code lists",
      points: [
        total ? `${total} candidate(s) were found in the data; ${open} still need your decision.` :
          "No candidates yet - discovery has not run for this entity.",
        "For each candidate: Accept it if the field holds codes from a fixed list (e.g. plant, material type), Reject it if it is free text, a number or a date.",
        "Items marked NEW appeared in the latest discovery; earlier decisions are kept automatically.",
        "When the list looks right, press Continue - the lookups are built from your decisions.",
      ],
    };
  }
  if (step === 2) {
    const s = ov?.step2?.summary || {};
    if (!ov?.step2?.present) {
      return {
        title: "Step 2 - client values (optional)",
        points: [
          "No client workbook has been loaded.",
          "If the client maintains these code lists in a workbook, choose it and press Upload & compare (right below the file pickers).",
          "No workbook? Press Skip - the lookups will use the values found in the data.",
        ],
      };
    }
    return {
      title: "Step 2 - match the client's code lists to the fields",
      points: [
        `${n(s.resolved)} field(s) matched a client list, ${n(s.needs_review)} need your review, ${n(s.unmatched)} found no good match.`,
        n(s.decisions_preserved) ? `${n(s.decisions_preserved)} of your earlier decisions were kept.` : "Your confirmations and rejections are kept when mappings are recomputed.",
        "For each mapping: Accept if the client list belongs to that field, Reject if it does not.",
        "A new workbook? Choose it and press Upload & compare below the file pickers first - Apply stays disabled until you do.",
        "Then press Apply client values & continue.",
      ],
    };
  }
  return {
    title: "Step 3 - review the lists and publish",
    points: [
      "Check each lookup: its accepted values, where they came from (data, client, both) and the share of rows that pass.",
      "Press Publish to EKD & BC. In the pop-up you choose which governance statuses count as accepted and whether to accept lists that were taken from the data.",
      "Nothing reaches Enterprise Knowledge or Business Controls until you publish.",
    ],
  };
}

// ---- buttons explained (per step) ---------------------------------------------------------
const BUTTONS: Record<Step, Array<{ b: string; what: string; behind: string }>> = {
  1: [
    { b: "Accept as lookup", what: "Confirms the field holds codes from a fixed list.", behind: "The field becomes part of a lookup entity (named as shown)." },
    { b: "Reject", what: "The field is not a code list.", behind: "It is left out; the decision is remembered on the next discovery." },
    { b: "Reopen", what: "Undo an accept / reject.", behind: "The candidate goes back to 'needs review'." },
    { b: "Accept all", what: "Accepts every open candidate in the current tab.", behind: "Same as Accept on each one." },
    { b: "Continue", what: "Finish Step 1.", behind: "Builds / refreshes the lookup entities from your decisions and corrects values with the latest profile." },
    { b: "Skip to Step 3", what: "No client workbook.", behind: "Builds the lookups and goes straight to review & publish." },
  ],
  2: [
    { b: "Upload & compare (below the file pickers)", what: "Process the chosen client workbook and/or dictionary.", behind: "Compares it with what is in place and opens the comparison. Nothing is saved until you accept the changes." },
    { b: "Accept / Reject (per mapping)", what: "Is this client list the right one for this field?", behind: "Accepted lists are attached to the field; rejected ones are never bound." },
    { b: "Apply client values & continue", what: "Finish Step 2.", behind: "Attaches the accepted client codes to the lookups, written the way each field stores them (e.g. 2 -> 02)." },
    { b: "Skip", what: "No client workbook.", behind: "Goes to Step 3; lookups use the values found in the data." },
    { b: "More > Re-open the upload comparison", what: "See the workbook comparison again.", behind: "Read-only until you accept a change." },
    { b: "More > Recompute mappings", what: "Recalculate all mapping proposals.", behind: "Your confirmations and rejections are kept; changed proposals are marked for review." },
  ],
  3: [
    { b: "Publish to EKD & BC", what: "Send the accepted lists downstream.", behind: "Opens the publish pop-up: choose accepted statuses, accept observed lists, see rows passing; then updates Enterprise Knowledge and re-checks Business Controls." },
    { b: "More > Harmonize entity names", what: "Make lookup names consistent.", behind: "Proposes renames; you approve them." },
    { b: "More > New lookup entity", what: "Create an empty lookup.", behind: "You then bind fields to it." },
    { b: "More > Rebuild entities", what: "Rebuild lookups from Step 1.", behind: "Same as Step 1 Continue." },
    { b: "More > Export", what: "Download the review (Excel / HTML / CSV).", behind: "Read-only." },
  ],
};

// ---- glossary --------------------------------------------------------------------------
const TERMS: Array<{ t: string; d: string }> = [
  { t: "Lookup (code list)", d: "A field whose values come from a fixed list of codes - e.g. plant, material type, allergen code." },
  { t: "Lookup entity", d: "One code list, which can be used by several fields (e.g. 'Plant' used by MARC.WERKS and MVKE.DWERK)." },
  { t: "Candidate", d: "A field the system thinks may be a lookup. You accept or reject it in Step 1." },
  { t: "NEW / no longer proposed", d: "NEW: appeared in the latest discovery. No longer proposed: you decided it earlier but discovery did not find it again; the decision is kept." },
  { t: "Dependent lookup", d: "Where one field's value decides the allowed values of another (e.g. country decides region)." },
  { t: "Code <-> display pair", d: "A code field with a matching description field (e.g. MTART and its text)." },
  { t: "Observed values", d: "The values actually found in the data. They are evidence, not a decision." },
  { t: "Client workbook / client values", d: "The code lists the client maintains themselves (usually Excel). Optional." },
  { t: "Mapping", d: "Which client list belongs to which field. Shown with a score; you accept or reject it." },
  { t: "Mapping score", d: "How well field and client list match (name similarity and shared values). A confirmed mapping always wins over a low score." },
  { t: "Apply client values", d: "Attaches the accepted client codes to the lookups (this used to be called 'fold')." },
  { t: "Code alignment", d: "Excel stores 01 as 1. Client codes are written the way the field stores them, so 1 matches 01. Recorded for audit." },
  { t: "Multi-value field", d: "A cell holding several codes joined by '|' (e.g. 01|M). Each code is checked on its own." },
  { t: "Measure field", d: "A number such as weight or length. Never treated as a code list." },
  { t: "Canonical value", d: "A value in the lookup's list, with its governance status." },
  { t: "Accepted list", d: "The values that count as valid in DQ rules: those promoted to Enterprise Knowledge." },
  { t: "Governance status", d: "Approved, Draft, Submitted For Approval, Candidate, Not Documented Yet, Obsolete - set per value by the client / SME." },
  { t: "Promote / Publish", d: "Choosing which statuses count as accepted and pushing the lists to Enterprise Knowledge and Business Controls." },
  { t: "Observed list / Accept as list", d: "A list taken entirely from the data, with no client source. Accepting it means: today's values are the valid ones. A rule on it finds no current defects - it only flags new codes." },
  { t: "Rows passing", d: "Share of the field's populated rows whose value is in the accepted list. Shown now and after your choice." },
  { t: "Binding", d: "The link between a field and its lookup entity." },
  { t: "Crosswalk", d: "How a value found in the data maps to a canonical value (e.g. two spellings to one code)." },
  { t: "EKD", d: "Enterprise Knowledge: the governed description of the data model that DQ rules are built from." },
  { t: "BC", d: "Business Controls: business rules derived from the knowledge, before they become DQ rules." },
];

export default function SmeHelp({ open, onClose, step, overview }: Props) {
  const [tab, setTab] = useState<"now" | "buttons" | "terms">("now");
  const [q, setQ] = useState("");
  const now = useMemo(() => nowFor(step, overview), [step, overview]);
  const terms = TERMS.filter((x) => !q || (x.t + " " + x.d).toLowerCase().includes(q.toLowerCase()));
  if (!open) return null;
  return (
    <div className="smh-ov" onClick={onClose}>
      <style>{CSS}</style>
      <div className="smh" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="SME Help">
        <div className="smh-h">
          <b>SME Help</b><span className="smh-step">Step {step}</span>
          <div className="smh-sp" />
          <button className="smh-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="smh-tabs">
          {([["now", "What to do now"], ["buttons", "Buttons explained"], ["terms", "Terms"]] as const).map(([k, l]) => (
            <button key={k} className={"smh-tab " + (tab === k ? "on" : "")} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
        <div className="smh-b">
          {tab === "now" && (
            <div>
              <div className="smh-t">{now.title}</div>
              <ol className="smh-ol">{now.points.map((p, i) => <li key={i}>{p}</li>)}</ol>
              <div className="smh-flow">
                <span className={step === 1 ? "on" : ""}>1 Discover</span><i>→</i>
                <span className={step === 2 ? "on" : ""}>2 Client values (optional)</span><i>→</i>
                <span className={step === 3 ? "on" : ""}>3 Review &amp; publish</span>
              </div>
            </div>
          )}
          {tab === "buttons" && (
            <table className="smh-tbl"><thead><tr><th>Button</th><th>What it is for</th><th>What happens</th></tr></thead>
              <tbody>{BUTTONS[step].map((r) => <tr key={r.b}><td><b>{r.b}</b></td><td>{r.what}</td><td>{r.behind}</td></tr>)}</tbody></table>
          )}
          {tab === "terms" && (
            <div>
              <input className="smh-q" placeholder="Search a term…" value={q} onChange={(e) => setQ(e.target.value)} />
              <dl className="smh-dl">{terms.map((x) => <React.Fragment key={x.t}><dt>{x.t}</dt><dd>{x.d}</dd></React.Fragment>)}</dl>
              {!terms.length && <div className="smh-mut">No term matches “{q}”.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.smh-ov{position:fixed;inset:0;background:rgba(20,32,43,.35);z-index:1000;display:flex;justify-content:flex-end}
.smh{width:min(620px,96vw);height:100%;background:#fff;box-shadow:-12px 0 32px rgba(20,32,43,.18);display:flex;flex-direction:column;font-family:'Inter',system-ui,sans-serif;color:#182430}
.smh-h{display:flex;align-items:center;gap:10px;padding:16px 20px;border-bottom:1px solid #dde3df}
.smh-h b{font-size:16px}.smh-step{font-size:11px;font-weight:700;color:#0e8a6e;background:#e2f2ec;border-radius:6px;padding:2px 8px}
.smh-sp{flex:1}.smh-x{border:none;background:transparent;font-size:22px;cursor:pointer;color:#5d6d68;line-height:1}
.smh-tabs{display:flex;gap:4px;padding:10px 20px 0;border-bottom:1px solid #dde3df}
.smh-tab{border:none;background:transparent;padding:9px 12px;font-size:13px;cursor:pointer;font-family:inherit;color:#5d6d68;border-bottom:2px solid transparent}
.smh-tab.on{color:#14202b;font-weight:600;border-bottom-color:#14202b}
.smh-b{padding:18px 20px;overflow:auto;font-size:13.5px;line-height:1.55}
.smh-t{font-weight:600;font-size:14.5px;margin-bottom:8px}
.smh-ol{padding-left:20px;margin:0 0 16px}.smh-ol li{margin-bottom:7px}
.smh-flow{display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:12px;color:#5d6d68}
.smh-flow span{border:1px solid #dde3df;border-radius:7px;padding:4px 9px}.smh-flow span.on{background:#14202b;color:#fff;border-color:#14202b}
.smh-flow i{font-style:normal;color:#8a9791}
.smh-tbl{width:100%;border-collapse:collapse;font-size:12.8px}
.smh-tbl th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#8a9791;padding:6px 8px;border-bottom:1px solid #dde3df}
.smh-tbl td{padding:8px;border-bottom:1px solid #eef2ef;vertical-align:top}
.smh-q{width:100%;border:1px solid #dde3df;border-radius:8px;padding:8px 11px;font-size:13px;font-family:inherit;margin-bottom:12px}
.smh-dl{margin:0}.smh-dl dt{font-weight:600;margin-top:10px}.smh-dl dd{margin:2px 0 0;color:#3c4a55}
.smh-mut{color:#8a9791;font-size:12.5px}
`;
